import { Alert, Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import PartsAIChatInput from "./PartsAIChatInput";
import PartsAIChatInputButtons from "./PartsAIChatInputButtons";
import { getErrorMessage } from "src/views/remote-resources/modals/CreateResourceModal";
import RecentChats from "src/views/assistant/components/splashScreen/RecentChats";
import { PageTopHelpButton } from "src/components/help";
import { renderChatKindBadge } from "./ChatKindChip";

type PartsAISplashScreenProps = {
  chatStreamError: string;
  isChatConnectionError: boolean;
  isChatLoading: boolean;
  isChatEnded: boolean;
  isTurnActive: boolean;
  terminateConnection: () => void;
  sendUserMessage: (text: string) => Promise<boolean>;
  placeholder?: string;
  /**
   * Parts AI dialogue kind this splash represents (run|search|comparison).
   */
  recentsKind?: string;
  /**
   * Product whose dialogues the Recent Chats strip lists. Defaults to
   * parts-ai; override when the splash's evaluations live under another
   * product (e.g. the comparison splash's Item Equivalency dialogues).
   */
  recentsProductKey?: string;
  /**
   * Overrides where the Recent Chats strip's "View more" link points.
   * Defaults to the page's own productConfig.basePath.
   */
  recentsBasePath?: string;
  /**
   * Optional content rendered between the chat input and the recent chats,
   * used by the comparison splash to show the two parts being compared.
   */
  headerContent?: ReactNode;
  /** Seeds the chat input with editable text (e.g. a comparison prompt). */
  initialInputValue?: string;
  /**
   * Optional page-top controls rendered beside the help button on the right
   * (e.g. the workflow persona-slots panel).
   */
  topActions?: ReactNode;
  /** Optional page-top controls anchored to the left (e.g. a Runs link). */
  topActionsLeft?: ReactNode;
  /**
   * Replaces the default Parts AI chat input. The comparison splash kicks off
   * an Item Equivalency workflow, whose composer must tag uploads with that
   * product and keep the attach path from creating a dialogue outside the
   * kickoff; the other Parts AI splashes keep the default.
   */
  chatInput?: ReactNode;
};

const PartsAISplashScreen = ({
  chatStreamError,
  isChatConnectionError,
  isChatLoading,
  isChatEnded,
  isTurnActive,
  terminateConnection,
  sendUserMessage,
  placeholder,
  recentsKind,
  recentsProductKey = "parts-ai",
  recentsBasePath,
  headerContent,
  initialInputValue,
  topActions,
  topActionsLeft,
  chatInput,
}: PartsAISplashScreenProps) => {
  return (
    <Box
      sx={{
        height: "inherit",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 3,
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          justifyContent: "center",
          alignSelf: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {topActionsLeft}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {topActions}
            <PageTopHelpButton productLabel="Parts AI" product="parts-ai" />
          </Box>
        </Box>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", mb: 3 }}
          flexWrap="wrap"
        >
          <img
            src="/images/nuclearn-n-full.png"
            alt="Nuclearn Logo"
            width={40}
            height={40}
          />
          <Typography sx={{ fontFamily: "Poppins", fontSize: "36px" }}>
            Parts AI
          </Typography>
        </Stack>
      </Box>

      {isChatConnectionError && (
        <Box sx={{ width: 700 }}>
          <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
            <Typography variant="body2">
              {getErrorMessage(chatStreamError) ||
                "An error occurred while connecting to the chat."}
            </Typography>
          </Alert>
        </Box>
      )}

      {headerContent && (
        <Box sx={{ width: "100%", maxWidth: 780, mt: 3, mb: 3 }}>
          {headerContent}
        </Box>
      )}

      {chatInput ?? (
        <PartsAIChatInput
          chatStreamError={chatStreamError}
          isChatConnectionError={isChatConnectionError}
          isChatLoading={isChatLoading}
          isChatEnded={isChatEnded}
          isTurnActive={isTurnActive}
          InputButtons={PartsAIChatInputButtons}
          isSplashScreen={true}
          terminateConnection={terminateConnection}
          sendUserMessage={sendUserMessage}
          placeholder={placeholder}
          initialValue={initialInputValue}
        />
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <RecentChats
          productKey={recentsProductKey}
          kind={recentsKind}
          recentsBasePath={recentsBasePath}
          renderChatBadge={renderChatKindBadge}
        />
      </Box>
    </Box>
  );
};

export default PartsAISplashScreen;
